import type { StoreApi } from 'zustand';

import { FileDownloadStatusEnum } from '../../enums/FileDownloadStatusEnum';
import { StorageItemKeyEnum } from '../../enums/StorageItemKeyEnum';
import { isDownloadCancelled } from './downloadCancellation';
import type ProcessStatus from '../../interfaces/ProcessStatus';
import type { TransferService } from '../../services/transferApiService';
import { getStorageItem, storeStorageItem } from '../../utils/storage';
import { getFileNameFromUrl } from './fileDownloadUtils';

const POLL_INTERVAL_MS = 2500;

const pollingIntervals = new Map<string, number>();

let transferService: TransferService | null = null;
let transferServicePromise: Promise<TransferService> | null = null;
let initialized = false;
let unsubscribeStore: (() => void) | null = null;

const getTransferService = async (): Promise<TransferService> => {
  if (transferService) {
    return transferService;
  }

  if (!transferServicePromise) {
    transferServicePromise = import('../../services/transferApiService').then((module) => {
      transferService = new module.TransferService();
      return transferService;
    });
  }

  return transferServicePromise;
};

export type FileDownloadPollingStore = StoreApi<{
  activeFileDownloadProcessIds: string[];
  authenticatedUser: unknown | null;
  setActiveFileDownloadProcessIds: (
    processIds: string[] | ((previousIds: string[]) => string[]),
  ) => void;
  setFileDownloadProcessStatuses: (
    statuses: ProcessStatus[] | ((previousStatuses: ProcessStatus[]) => ProcessStatus[]),
  ) => void;
  token: string | null;
  upsertFileDownloadProcess: (process: ProcessStatus) => void;
}>;

export const pollProcess = async (processId: string): Promise<void> => {
  const {
    setActiveFileDownloadProcessIds,
    setFileDownloadProcessStatuses,
    upsertFileDownloadProcess,
  } = pollingStore?.getState() ?? {};

  if (
    !setActiveFileDownloadProcessIds ||
    !setFileDownloadProcessStatuses ||
    !upsertFileDownloadProcess
  ) {
    return;
  }

  try {
    const service = await getTransferService();
    const response = await service.getProcessStatus(processId);
    const updatedProcess: ProcessStatus = {
      ...response,
      processId,
    };
    upsertFileDownloadProcess(updatedProcess);

    if (response.status !== FileDownloadStatusEnum.Processing) {
      stopPollingForProcess(processId);

      if (isDownloadCancelled(processId)) {
        setActiveFileDownloadProcessIds((previousIds) =>
          previousIds.filter((id) => id !== processId),
        );
        return;
      }

      setActiveFileDownloadProcessIds((previousIds) =>
        previousIds.filter((id) => id !== processId),
      );

      const hasFailedFiles =
        Array.isArray(response.failedFilesList) && response.failedFilesList.length > 0;

      if (response.status === FileDownloadStatusEnum.Success && response.additionalData) {
        const downloadUrl = response.additionalData;
        const fileName = getFileNameFromUrl(downloadUrl);

        upsertFileDownloadProcess({
          ...response,
          processId,
          name: fileName,
          status: hasFailedFiles
            ? FileDownloadStatusEnum.PartialSuccess
            : FileDownloadStatusEnum.Success,
        });

        try {
          const url = new URL(downloadUrl);
          const expirationParam = url.searchParams.get('se');
          if (expirationParam) {
            const expirationTime = new Date(expirationParam);
            const timeUntilExpiration = expirationTime.getTime() - Date.now();

            if (timeUntilExpiration > 0) {
              setTimeout(() => {
                setFileDownloadProcessStatuses((previousStatuses) =>
                  previousStatuses.filter((status) => status.processId !== processId),
                );
              }, timeUntilExpiration);
            } else {
              setFileDownloadProcessStatuses((previousStatuses) =>
                previousStatuses.filter((status) => status.processId !== processId),
              );
            }
          } else {
            console.warn(`'se' parameter not found in URL for process ${processId}`);
          }
        } catch (error) {
          console.error(`Invalid URL in additionalData for process ${processId}:`, error);
        }

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  } catch (error) {
    console.error(`Error getting status for processId ${processId}:`, error);
    stopPollingForProcess(processId);
    setActiveFileDownloadProcessIds((previousIds) => previousIds.filter((id) => id !== processId));
    upsertFileDownloadProcess({
      additionalData: '',
      processId,
      progressPercentage: 0,
      status: FileDownloadStatusEnum.Failed,
    });
  }
};

let pollingStore: FileDownloadPollingStore | null = null;

export const startPollingForProcess = (processId: string): void => {
  if (pollingIntervals.has(processId)) {
    return;
  }

  void pollProcess(processId);
  const intervalId = window.setInterval(() => {
    void pollProcess(processId);
  }, POLL_INTERVAL_MS);
  pollingIntervals.set(processId, intervalId);
};

export const stopPollingForProcess = (processId: string): void => {
  const intervalId = pollingIntervals.get(processId);
  if (intervalId !== undefined) {
    clearInterval(intervalId);
    pollingIntervals.delete(processId);
  }
};

export const syncPollingWithActiveIds = (activeProcessIds: string[]): void => {
  activeProcessIds.forEach((processId) => {
    startPollingForProcess(processId);
  });

  pollingIntervals.forEach((intervalId, processId) => {
    if (!activeProcessIds.includes(processId)) {
      clearInterval(intervalId);
      pollingIntervals.delete(processId);
    }
  });
};

export const stopAllFileDownloadPolling = (): void => {
  pollingIntervals.forEach((intervalId) => clearInterval(intervalId));
  pollingIntervals.clear();
};

const persistActiveProcessIds = async (activeProcessIds: string[]): Promise<void> => {
  await storeStorageItem('localStorage', StorageItemKeyEnum.ActiveProcessesIds, activeProcessIds);
};

const restoreActiveProcessIdsFromStorage = async (
  store: FileDownloadPollingStore,
): Promise<void> => {
  const savedState = await getStorageItem('localStorage', StorageItemKeyEnum.ActiveProcessesIds);
  if (savedState !== null) {
    store.getState().setActiveFileDownloadProcessIds(savedState);
  }
};

export const initFileDownloadPolling = (store: FileDownloadPollingStore): void => {
  if (initialized) {
    return;
  }
  initialized = true;
  pollingStore = store;

  void restoreActiveProcessIdsFromStorage(store).then(() => {
    syncPollingWithActiveIds(store.getState().activeFileDownloadProcessIds);
  });

  syncPollingWithActiveIds(store.getState().activeFileDownloadProcessIds);

  unsubscribeStore = store.subscribe((state, previousState) => {
    if (state.activeFileDownloadProcessIds !== previousState.activeFileDownloadProcessIds) {
      syncPollingWithActiveIds(state.activeFileDownloadProcessIds);
      void persistActiveProcessIds(state.activeFileDownloadProcessIds);
    }

    const wasAuthenticated = !!(previousState.authenticatedUser && previousState.token);
    const isAuthenticated = !!(state.authenticatedUser && state.token);
    if (wasAuthenticated && !isAuthenticated) {
      stopAllFileDownloadPolling();
    }
  });
};

export const resetFileDownloadPollingForTests = (): void => {
  stopAllFileDownloadPolling();
  if (unsubscribeStore) {
    unsubscribeStore();
    unsubscribeStore = null;
  }
  initialized = false;
  pollingStore = null;
  transferService = null;
  transferServicePromise = null;
};

export const setFileDownloadTransferServiceForTests = (service: TransferService): void => {
  transferService = service;
};
